/*
 * PII scrub (spec §4.5). Runs on every payload before it leaves the browser:
 * strips query params from URLs and drops any field matching the PII allowlist —
 * at every depth, so a PII value nested inside an object or an array of records
 * is removed too, not only a top-level key.
 */

const PII_FIELDS = new Set([
  'name',
  'displayName',
  'email',
  'phone',
  'ssn',
  'taxId',
  'tin',
  'address',
  'password',
]);

export function scrubUrl(url: string): string {
  try {
    const u = new URL(url, 'http://local');
    return u.pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively drop PII keys from any nested object/array while preserving the
 * surrounding structure. Arrays keep their length and index positions (each
 * element is scrubbed); non-object primitives pass through unchanged.
 */
function scrubValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value)) {
      if (PII_FIELDS.has(key)) continue;
      out[key] = scrubValue(nested);
    }
    return out;
  }
  return value;
}

export function scrubData(
  data: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!data) return data;
  return scrubValue(data) as Record<string, unknown>;
}
